/* PIPOP v41 cart drawer state synchronization. */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.drawerBound === 'true') return;

    cartLink.dataset.drawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
      this.setAttribute('aria-hidden', 'false');
    });

    const focusTarget = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    if (focusTarget) {
      setTimeout(() => {
        const focusContainer = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');
        if (focusContainer) trapFocus(focusContainer, focusTarget);
      }, 280);
    }

    document.body.classList.add('overflow-hidden', 'pipop-cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'pipop-cart-drawer-open');
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState.sections && parsedState.sections[section.id];
      if (!target || !html) return;

      const source = this.getSectionDOM(html, section.selector || '.shopify-section');
      if (source) target.innerHTML = source.innerHTML;
    });

    this.classList.remove('is-empty');
    this.bindOverlay();
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = this.getSectionDOM(html, selector);
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.lineQueues = new Map();
  }

  /* Drawer quantities are submitted to Shopify directly. Browser pop-up validation is intentionally avoided. */
  validateQuantity(event) {
    const quantity = Number.parseInt(event.target.value, 10);
    if (!Number.isFinite(quantity) || quantity < 0) return;

    event.target.setCustomValidity('');
    this.updateQuantity(
      event.target.dataset.index,
      quantity,
      event,
      event.target.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const row =
      event?.target?.closest?.('.cart-item') ||
      event?.currentTarget?.closest?.('.cart-item') ||
      this.querySelector(`#CartDrawer-Item-${line}`);
    const itemKey = row?.dataset.itemKey;

    if (!row || !itemKey) return;

    const queue = this.lineQueues.get(itemKey) || { running: false, next: null };
    queue.next = { line: Number(line), quantity: Number(quantity), row, itemKey, variantId };
    this.lineQueues.set(itemKey, queue);

    if (!queue.running) this.drainLineQueue(itemKey);
  }

  async drainLineQueue(itemKey) {
    const queue = this.lineQueues.get(itemKey);
    if (!queue) return;

    queue.running = true;
    while (queue.next) {
      const job = queue.next;
      queue.next = null;
      await this.submitQuantity(job);
    }
    queue.running = false;
    this.lineQueues.delete(itemKey);
  }

  async submitQuantity(job) {
    const { row, itemKey, quantity, variantId } = job;
    this.setRowBusy(row, true);
    this.clearRowError(row);

    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: itemKey, quantity }),
      });
      const cart = await response.json();

      if (!response.ok || cart.errors || cart.status) {
        const message = cart.description || cart.message || cart.errors || window.cartStrings.error;
        throw new Error(typeof message === 'string' ? message : window.cartStrings.error);
      }

      this.updateSharedCartState(cart);

      const currentItem = cart.items.find((item) => item.key === itemKey);
      if (quantity === 0 || !currentItem) {
        await this.refreshDrawer(cart);
      } else {
        this.updateLineItem(row, currentItem, cart.currency);
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: cart,
        variantId,
      });
    } catch (error) {
      this.showRowError(row, error.message || window.cartStrings.error);
      const select = row.querySelector('.pipop-cart-qty-input');
      if (select) select.value = select.getAttribute('value');
    } finally {
      if (row.isConnected) this.setRowBusy(row, false);
    }
  }

  updateLineItem(row, item, currency) {
    const select = row.querySelector('.pipop-cart-qty-input');
    if (select) {
      select.value = String(item.quantity);
      select.setAttribute('value', String(item.quantity));
    }

    const linePrice = row.querySelector('[data-cart-line-price]');
    if (linePrice) linePrice.textContent = this.formatMoney(item.final_line_price, currency);
  }

  updateSharedCartState(cart) {
    const drawerCount = document.querySelector('[data-cart-drawer-count]');
    if (drawerCount) drawerCount.textContent = String(cart.item_count);

    const subtotal = document.querySelector('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = this.formatMoney(cart.total_price, cart.currency);

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.classList.toggle('is-empty', cart.item_count === 0);

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    let bubble = cartLink.querySelector('.cart-count-bubble');
    if (cart.item_count === 0) {
      if (bubble) bubble.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      cartLink.appendChild(bubble);
    }

    bubble.innerHTML = `
      ${cart.item_count < 100 ? `<span aria-hidden="true">${cart.item_count}</span>` : ''}
      <span class="visually-hidden">${cart.item_count} items</span>
    `;
  }

  async refreshDrawer(cart) {
    const response = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(window.cartStrings.error);

    const sections = await response.json();
    const drawerHTML = sections['cart-drawer'];
    const bubbleHTML = sections['cart-icon-bubble'];
    const targetInner = document.querySelector('#CartDrawer .drawer__inner');
    const cartDrawer = document.querySelector('cart-drawer');
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (drawerHTML && targetInner) {
      const drawerDocument = new DOMParser().parseFromString(drawerHTML, 'text/html');
      const sourceInner = drawerDocument.querySelector('#CartDrawer .drawer__inner');
      if (sourceInner) targetInner.replaceWith(sourceInner);
    }

    if (bubbleHTML && cartLink) {
      const bubbleDocument = new DOMParser().parseFromString(bubbleHTML, 'text/html');
      const sourceSection = bubbleDocument.querySelector('.shopify-section');
      if (sourceSection) cartLink.innerHTML = sourceSection.innerHTML;
    }

    if (cartDrawer) cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
  }

  formatMoney(cents, currency) {
    const activeCurrency = currency || window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrency,
        currencyDisplay: 'symbol',
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${activeCurrency} ${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  setRowBusy(row, busy) {
    row.classList.toggle('is-updating', busy);
    row.setAttribute('aria-busy', busy ? 'true' : 'false');
    row.querySelectorAll('select, button').forEach((control) => {
      control.disabled = busy;
    });
  }

  clearRowError(row) {
    const error = row.querySelector('.cart-item__error-text');
    if (error) error.textContent = '';
  }

  showRowError(row, message) {
    const error = row.querySelector('.cart-item__error-text');
    if (error) error.textContent = message;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
